import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getBackendUrl } from '../config';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

const BACKEND_URL = getBackendUrl();

const REPORT_COMPANY_ALL = '__ALL__';
/** Must match backend FLEET_REPORT_COMPANY_ID (single consolidated report for all companies). */
const FLEET_REPORT_COMPANY_ID = '__ALL_COMPANIES__';

function escapeHtml(text) {
  if (text == null) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** YYYY-MM-DD string → UTC midnight ms for comparisons */
function dateStrToUtcMs(s) {
  if (!s) return null;
  const part = String(s).split('T')[0];
  const p = part.split('-').map(Number);
  if (p.length !== 3 || p.some((n) => Number.isNaN(n))) return null;
  return Date.UTC(p[0], p[1] - 1, p[2]);
}

/** Shfaqje e energjisë në MWh (nga kWh). */
function formatEnergyMWhFromKwh(kwh) {
  const n = Number(kwh);
  if (!Number.isFinite(n)) return '—';
  const mwh = n / 1000;
  return mwh.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

const Reports = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [reports, setReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [listFilterFrom, setListFilterFrom] = useState('');
  const [listFilterTo, setListFilterTo] = useState('');
  const [listSearch, setListSearch] = useState('');
  const [formData, setFormData] = useState({
    report_type: 'DAILY',
    period_start: '',
    period_end: '',
    company_id: ''
  });

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const fetchReports = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setCompanies(list);
      if (list.length > 0) {
        setFormData((prev) => {
          if (prev.company_id) return prev;
          return { ...prev, company_id: list[0].id };
        });
      }
    } catch (error) {
      setCompanies([]);
      console.error('Error fetching companies:', error);
    }
  }, []);

  useEffect(() => {
    fetchReports();
    fetchCompanies();
  }, [fetchReports, fetchCompanies]);

  const getCompanyName = useCallback(
    (companyId) => {
      if (companyId === FLEET_REPORT_COMPANY_ID) return t('reportFleetCompanyLabel');
      return companies.find((c) => String(c.id) === String(companyId))?.name || '-';
    },
    [companies, t]
  );

  const reportTypeLabelLower = useCallback((type) => {
    const label = (() => {
      switch (type) {
        case 'DAILY': return t('daily');
        case 'WEEKLY': return t('weeklyReport');
        case 'MONTHLY': return t('monthly');
        case 'YEARLY': return t('yearly');
        default: return String(type || '');
      }
    })();
    return label.toLowerCase();
  }, [t]);

  const displayReports = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    const filtered = reports.filter((r) => {
      const rs = dateStrToUtcMs(r.period_start);
      const re = dateStrToUtcMs(r.period_end);
      if (listFilterFrom) {
        const f = dateStrToUtcMs(listFilterFrom);
        if (f != null && re != null && re < f) return false;
      }
      if (listFilterTo) {
        const toMs = dateStrToUtcMs(listFilterTo);
        if (toMs != null && rs != null && rs > toMs) return false;
      }
      if (!q) return true;
      const typeLabel = reportTypeLabelLower(r.report_type);
      const comp = getCompanyName(r.company_id).toLowerCase();
      return (
        typeLabel.includes(q) ||
        comp.includes(q) ||
        String(r.period_start || '').toLowerCase().includes(q) ||
        String(r.period_end || '').toLowerCase().includes(q) ||
        String(r.report_type || '').toLowerCase().includes(q)
      );
    });
    return [...filtered].sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return tb - ta;
    });
  }, [reports, listFilterFrom, listFilterTo, listSearch, getCompanyName, reportTypeLabelLower]);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const token = localStorage.getItem('access_token');
      const basePayload = {
        report_type: formData.report_type,
        period_start: formData.period_start,
        period_end: formData.period_end,
      };

      if (formData.company_id === REPORT_COMPANY_ALL && companies.length > 1) {
        await axios.post(
          `${BACKEND_URL}/api/reports`,
          { ...basePayload, all_companies: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setShowModal(false);
        fetchReports();
        toast.success(t('reportFleetGenerated'));
        return;
      }

      let companyId = formData.company_id;
      if (!companyId && companies.length > 0) companyId = companies[0].id;
      if (!companyId && user) {
        companyId = user.company_id || (user.company_ids && user.company_ids[0]);
      }
      if (!companyId) {
        toast.error(t('selectCompanyFirst'));
        return;
      }
      const payload = { ...basePayload, company_id: companyId };
      await axios.post(`${BACKEND_URL}/api/reports`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      fetchReports();
      toast.success(t('reportGenerated'));
    } catch (error) {
      console.error('Error generating report:', error);
      const msg = error.response?.data?.detail ?? error.message ?? t('reportError');
      toast.error(Array.isArray(msg) ? msg.map((m) => m.msg || m).join(', ') : msg);
    } finally {
      setGenerating(false);
    }
  };

  const confirmDeleteReport = async () => {
    if (!deleteTarget) return;
    const report = deleteTarget;
    setDeletingId(report.id);
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${BACKEND_URL}/api/reports/${report.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports((prev) => prev.filter((r) => r.id !== report.id));
      setDeleteTarget(null);
      toast.success(t('reportDeleted'));
    } catch (error) {
      console.error('Error deleting report:', error);
      const msg = error.response?.data?.detail ?? error.message ?? t('reportDeleteError');
      toast.error(typeof msg === 'string' ? msg : t('reportDeleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleExportPDF = (report) => {
    const resolved = getCompanyName(report.company_id);
    const companyName = resolved === '-' ? 'DIO Energy' : resolved;
    const isFleet = String(report.company_id) === FLEET_REPORT_COMPANY_ID;
    const byCompany = Array.isArray(report.by_company) ? report.by_company : [];

    const prod = (v) => (Number(v) || 0).toLocaleString();

    const isHibSingleCoReport =
      report.grid_import_kwh != null || report.grid_export_kwh != null;

    const hibGridSingleRows = isHibSingleCoReport
      ? `
          <tr>
            <td>${escapeHtml(t('reportGridExportKwhLabel'))}</td>
            <td>${prod(report.grid_export_kwh ?? report.total_production_kwh)} kWh</td>
          </tr>
          <tr>
            <td>${escapeHtml(t('reportGridImportKwhLabel'))}</td>
            <td>${prod(report.grid_import_kwh ?? 0)} kWh</td>
          </tr>`
      : '';

    const productionOnlyRow = !isHibSingleCoReport
      ? `
          <tr>
            <td>Prodhimi Total</td>
            <td>${prod(report.total_production_kwh)} kWh</td>
          </tr>`
      : '';

    const singleCompanyBody = `
        <div class="company">Kompania: ${escapeHtml(companyName)}</div>
        <h1>Raport i Energjisë</h1>
        <p><strong>Periudha:</strong> ${escapeHtml(report.period_start)} - ${escapeHtml(report.period_end)}</p>
        <table>
          <tr>
            <th>Metrika</th>
            <th>Vlera</th>
          </tr>
          ${productionOnlyRow}
          ${hibGridSingleRows}
        </table>`;

    const hasHibFleetCols = byCompany.some(
      (row) => row.grid_export_kwh != null || row.grid_import_kwh != null
    );
    const fleetTotalExportKwh = hasHibFleetCols
      ? byCompany.reduce((s, row) => s + (Number(row.grid_export_kwh) || 0), 0)
      : 0;
    const fleetTotalImportKwh = hasHibFleetCols
      ? byCompany.reduce((s, row) => s + (Number(row.grid_import_kwh) || 0), 0)
      : 0;

    const fleetColgroup = hasHibFleetCols
      ? `<colgroup><col /><col style="width: 22%" /><col style="width: 22%" /><col style="width: 22%" /></colgroup>`
      : `<colgroup><col /><col style="width: 32%" /></colgroup>`;

    const fleetHeaderExtras = hasHibFleetCols
      ? `<th class="num">${escapeHtml(t('reportFleetTableExportKwh'))}</th><th class="num">${escapeHtml(t('reportFleetTableImportKwh'))}</th>`
      : '';

    const fleetBody =
      isFleet && byCompany.length > 0
        ? `
        <h1>Raport i përbashkët (të gjitha kompanitë)</h1>
        <p><strong>Periudha:</strong> ${escapeHtml(report.period_start)} - ${escapeHtml(report.period_end)}</p>
        <p><strong>Tipi:</strong> ${escapeHtml(report.report_type)}</p>

        <h2 class="section-title">Prodhimi sipas kompanive (MWh)</h2>
        <table class="fleet-mwh-table">
          ${fleetColgroup}
          <tr><th>Kompania</th><th class="num">Prodhimi (MWh)</th>${fleetHeaderExtras}</tr>
          ${byCompany
          .map(
            (row) => `
          <tr>
            <td>${escapeHtml(row.company_name || row.company_id)}</td>
            <td class="num">${escapeHtml(formatEnergyMWhFromKwh(row.total_production_kwh))}</td>
            ${hasHibFleetCols
    ? `<td class="num">${row.grid_export_kwh != null ? escapeHtml(prod(row.grid_export_kwh)) : '—'}</td><td class="num">${row.grid_import_kwh != null ? escapeHtml(prod(row.grid_import_kwh)) : '—'}</td>`
    : ''}
          </tr>`
          )
          .join('')}
          <tr class="total-row">
            <td><strong>Total</strong></td>
            <td class="num"><strong>${escapeHtml(formatEnergyMWhFromKwh(report.total_production_kwh))}</strong></td>
            ${hasHibFleetCols
    ? `<td class="num"><strong>${escapeHtml(prod(fleetTotalExportKwh))}</strong></td><td class="num"><strong>${escapeHtml(prod(fleetTotalImportKwh))}</strong></td>`
    : ''}
          </tr>
        </table>`
        : null;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Raport ${escapeHtml(report.report_type)} - ${escapeHtml(report.period_start)} deri ${escapeHtml(report.period_end)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; }
          h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .logo { font-size: 24px; font-weight: bold; color: #1e40af; }
          .meta { color: #666; font-size: 14px; }
          .company { font-size: 18px; font-weight: bold; color: #333; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          th { background: #f3f4f6; font-weight: 600; }
          td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
          tr.total-row td { background: #f3f4f6; font-weight: 600; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px; }
          .section-title { font-size: 17px; color: #0f172a; margin-top: 24px; margin-bottom: 10px; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px; }
          table.fleet-mwh-table { table-layout: fixed; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">DIO IOT SCADA</div>
          <div class="meta">
            <div>Data: ${new Date().toLocaleDateString('sq-AL')}</div>
            <div>Tipi: ${escapeHtml(report.report_type)}</div>
          </div>
        </div>
        ${fleetBody || singleCompanyBody}
        <div class="footer">
          <p>Gjeneruar automatikisht nga DIO IOT SCADA</p>
          <p>© ${new Date().getFullYear()} DIO Energy Solutions</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const getReportTypeLabel = (type) => {
    switch (type) {
      case 'DAILY': return t('daily');
      case 'WEEKLY': return t('weeklyReport');
      case 'MONTHLY': return t('monthly');
      case 'YEARLY': return t('yearly');
      default: return type;
    }
  };

  const getReportTypeColor = (type) => {
    switch (type) {
      case 'DAILY': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'WEEKLY': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'MONTHLY': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'YEARLY': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const setDefaultDates = (type) => {
    const today = new Date();
    let start, end;

    switch (type) {
      case 'DAILY':
        start = today.toISOString().split('T')[0];
        end = start;
        break;
      case 'WEEKLY':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = weekStart.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'MONTHLY':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'YEARLY':
        start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      default:
        start = today.toISOString().split('T')[0];
        end = start;
    }

    setFormData(prev => ({ ...prev, report_type: type, period_start: start, period_end: end }));
  };

  return (
    <Layout>
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{t('reportsTitle')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('reportsSubtitle')}</p>
          </div>
          <button
            onClick={() => {
              setDefaultDates('DAILY');
              setShowModal(true);
            }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
            data-testid="generate-report-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{t('generateReport')}</span>
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('totalReports')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{reports.length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('daily')}</p>
            <p className="text-2xl font-bold text-blue-600">{reports.filter(r => r.report_type === 'DAILY').length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('monthly')}</p>
            <p className="text-2xl font-bold text-purple-600">{reports.filter(r => r.report_type === 'MONTHLY').length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('yearly')}</p>
            <p className="text-2xl font-bold text-orange-600">{reports.filter(r => r.report_type === 'YEARLY').length}</p>
          </div>
        </div>

        {!loading && reports.length > 0 && (
          <div className="mb-4 flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="w-full text-xs font-medium text-gray-600 dark:text-gray-300 sm:mb-0">
              {t('reportsFilterListPeriod')}
            </div>
            <div className="flex min-w-[140px] flex-1 flex-col gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('startDate')}</span>
              <input
                type="date"
                value={listFilterFrom}
                onChange={(e) => setListFilterFrom(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="flex min-w-[140px] flex-1 flex-col gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('endDate')}</span>
              <input
                type="date"
                value={listFilterTo}
                onChange={(e) => setListFilterTo(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="flex min-w-[180px] flex-[2] flex-col gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('search')}</span>
              <input
                type="search"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder={t('reportsSearchPlaceholder')}
                className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setListFilterFrom('');
                setListFilterTo('');
                setListSearch('');
              }}
              className="h-10 shrink-0 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {t('reportsClearFilters')}
            </button>
          </div>
        )}

        {/* Reports List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">{t('loading')}</div>
          ) : reports.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">{t('noReportsGenerated')}</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">{t('generateFirstReport')}</p>
            </div>
          ) : displayReports.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">{t('reportsNoFilterResults')}</div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {displayReports.map((report) => (
                <div
                  key={report.id}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                  data-testid={`report-${report.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getReportTypeColor(report.report_type)}`}>
                          {getReportTypeLabel(report.report_type)}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {report.period_start} - {report.period_end}
                        </span>
                        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                          {getCompanyName(report.company_id)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
                        {!(
                          report.grid_export_kwh != null || report.grid_import_kwh != null
                        ) && (
                          <div>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{t('productionLabel')}</p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {Number(report.total_production_kwh).toLocaleString()} kWh
                            </p>
                          </div>
                        )}
                        {report.grid_export_kwh != null && (
                          <div>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{t('reportFleetTableExportKwh')}</p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {Number(report.grid_export_kwh).toLocaleString()} kWh
                            </p>
                          </div>
                        )}
                        {report.grid_import_kwh != null && (
                          <div>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{t('reportFleetTableImportKwh')}</p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {Number(report.grid_import_kwh).toLocaleString()} kWh
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleExportPDF(report)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        {t('pdf')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(report)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition font-medium"
                        data-testid={`delete-report-${report.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {t('deleteReport')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <AlertDialog
          open={!!deleteTarget}
          onOpenChange={(open) => {
            if (!open && !deletingId) setDeleteTarget(null);
          }}
        >
          <AlertDialogContent className="z-[100] max-w-md border border-gray-200 bg-white shadow-xl dark:border-gray-600 dark:bg-gray-800 sm:rounded-2xl">
            <AlertDialogHeader className="sm:text-left">
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40 sm:mx-0">
                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <AlertDialogTitle className="text-gray-900 dark:text-white">
                {t('deleteReportDialogTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-600 dark:text-gray-300">
                {t('confirmDeleteReport')}
              </AlertDialogDescription>
              {deleteTarget && (
                <div className="rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-800 dark:bg-gray-700/80 dark:text-gray-200">
                  {getReportTypeLabel(deleteTarget.report_type)} · {deleteTarget.period_start} – {deleteTarget.period_end}
                </div>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-2">
              <AlertDialogCancel
                disabled={!!deletingId}
                className="border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
              >
                {t('cancel')}
              </AlertDialogCancel>
              <Button
                type="button"
                variant="destructive"
                disabled={!!deletingId}
                className="bg-red-600 hover:bg-red-700 focus-visible:ring-red-600"
                onClick={() => confirmDeleteReport()}
              >
                {deletingId ? t('loading') : t('deleteReport')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Generate Report Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-sm md:max-w-md shadow-xl max-h-[90vh] overflow-y-auto flex flex-col">
              <div className="flex items-center justify-between p-4 md:p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10 shrink-0">
                <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">{t('generateNewReport')}</h2>
                <button type="button" onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600" aria-label={t('close')}>✕</button>
              </div>
              <form onSubmit={handleGenerateReport} className="p-4 md:p-4 space-y-4 md:space-y-4">
                {companies.length > 0 && (
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mb-1">{t('company')}</label>
                    <select
                      value={formData.company_id}
                      onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                      className="w-full px-3 md:px-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm h-10 md:py-2 md:h-auto"
                      data-testid="company-select"
                    >
                      {companies.length > 1 && (
                        <option value={REPORT_COMPANY_ALL}>
                          {isSuperAdmin ? t('reportAllCompanies') : t('reportAllCompaniesMyAccess')}
                        </option>
                      )}
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>{company.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mb-2">{t('reportType')}</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].map((type) => (
                      <button key={type} type="button" onClick={() => setDefaultDates(type)}
                        className={`px-3 md:px-3 py-3 md:py-2 rounded-lg text-xs md:text-sm font-medium transition ${formData.report_type === type ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}>
                        {getReportTypeLabel(type)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mb-1">{t('startDate')}</label>
                  <input type="date" value={formData.period_start} onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                    className="w-full px-3 md:px-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm h-10 md:py-2 md:h-auto" required />
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mb-1">{t('endDate')}</label>
                  <input type="date" value={formData.period_end} onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                    className="w-full px-3 md:px-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm h-10 md:py-2 md:h-auto" required />
                </div>
                <div className="flex gap-3 pt-4 md:pt-2">
                  <button type="button" onClick={() => setShowModal(false)}
                    className="flex-1 h-10 md:py-2 px-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium">{t('cancel')}</button>
                  <button type="submit" disabled={generating}
                    className="flex-1 h-10 md:py-2 px-3 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50" data-testid="confirm-generate-btn">
                    {generating ? t('generating') : t('generate')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
